import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "academic_journal_articles";

const JournalPublications = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    year: null,
    title: "",
    journal_name: "",
    doi_link: "",
    quartile: "",
    scopus_category: "",
    abdc: "",
    abs: "",
    wos_jif: "",
    total_authors: "",
    author_position: "",
    is_collaborative: "",
    collaborative_authors: "",
  });
  const [recordId, setRecordId] = useState(null);
  const [showCollaborativeAuthors, setShowCollaborativeAuthors] =
    useState(false);

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  const fetchRecords = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch records");
      const data = await res.json();

const sortedData = Array.isArray(data)
  ? data.sort((a, b) => {
      if (!a.year) return 1;
      if (!b.year) return -1;
      return new Date(b.year).getTime() - new Date(a.year).getTime();
    })
  : [];

setRecords(sortedData);
    } catch (err) {
      toast.error(`Fetch error: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthorPositionChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      author_position: value, // just save whatever is selected
    }));
  };

  const handleCollaborativeChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      is_collaborative: value,
      collaborative_authors: value === "Yes" ? "" : "",
    }));
    setShowCollaborativeAuthors(value === "Yes");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        year: formData.year ? format(formData.year, "yyyy-MM") + "-01" : null,
      };
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save record");

      toast.success(recordId ? "Record updated!" : "Record added!");
      setFormData({
        year: null,
        title: "",
        journal_name: "",
        doi_link: "",
        quartile: "",
        scopus_category: "",
        abdc: "",
        abs: "",
        wos_jif: "",
        total_authors: "",
        author_position: "",
        is_collaborative: "",
        collaborative_authors: "",
      });
      setRecordId(null);
      setShowCollaborativeAuthors(false);
      fetchRecords();
    } catch (err) {
      toast.error(`Submit error: ${err.message}`);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      year: rec.year ? new Date(rec.year) : null,
      title: rec.title || "",
      journal_name: rec.journal_name || "",
      doi_link: rec.doi_link || "",
      quartile: rec.quartile || "",
      scopus_category: rec.scopus_category || "",
      abdc: rec.abdc || "",
      abs: rec.abs || "",
      wos_jif: rec.wos_jif || "",
      total_authors: rec.total_authors || "",
      author_position: rec.author_position || "", // keeps "Other" if stored
      is_collaborative: rec.is_collaborative || "",
      collaborative_authors: rec.collaborative_authors || "",
    });
    setShowCollaborativeAuthors(rec.is_collaborative === "Yes");
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?{" "}
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const formatDate = (date) => (date ? format(new Date(date), "MM/yyyy") : "");
  const fieldLabels = {
    title: "Title",
    journal_name: "Journal Name",
    doi_link: "DOI Link",
    total_authors: "Total Authors",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Academic Journal Articles
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Year */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Publication Year (mm/yy){" "}
              <span
                style={{
                  fontWeight: 300,
                  fontSize: "0.85em",
                  marginLeft: "6px",
                  color: "#666",
                }}
              >
                (start with latest journal)
              </span>
            </label>
            <DatePicker
              selected={formData.year}
              onChange={(date) =>
                setFormData((prev) => ({ ...prev, year: date }))
              }
              dateFormat="MM/yyyy"
              showMonthYearPicker
              placeholderText="Select month/year"
              style={inputStyle}
            />
          </div>

          {/* Basic Text Fields */}
          {["title", "journal_name", "doi_link", "total_authors"].map((f) => (
            <div key={f} style={{ marginTop: "10px" }}>
              <label style={{ fontWeight: "bold" }}>{fieldLabels[f]}</label>
              <input
                type="text"
                name={f}
                value={formData[f]}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </div>
          ))}

          {/* Author Position */}
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontWeight: "bold" }}>Author Position</label>
            <select
              name="author_position"
              value={formData.author_position}
              onChange={handleAuthorPositionChange}
              style={inputStyle}
              required
            >
              <option value="">Select</option>
              {["1", "2", "3", "4", "5", "Other"].map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Collaborative */}
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontWeight: "bold" }}>
              Collaborative with BBS author/colleague?
            </label>
            <select
              name="is_collaborative"
              value={formData.is_collaborative}
              onChange={handleCollaborativeChange}
              style={inputStyle}
              required
            >
              <option value="">Select</option>
              {["Yes", "No"].map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {showCollaborativeAuthors && (
              <input
                type="text"
                placeholder="Enter author name(s)"
                value={formData.collaborative_authors}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    collaborative_authors: e.target.value,
                  }))
                }
                style={{ ...inputStyle, marginTop: "5px" }}
                required
              />
            )}
          </div>

          {/* Dropdowns */}
          {[
            {
              label: "Scopus Rank (Quartile)",
              name: "quartile",
              options: [
                "Q1: Top 10% (90th percentile or above)",
                "Q1: 75th to 89th percentile",
                "Q2: 50th to 74th percentile",
                "Q3: 25th to 49th percentile",
                "Q4: 0 to 24th percentile",
                "Not Applicable",
              ],
            },
            {
              label: "Scopus Category",
              name: "scopus_category",
              options: [
                "Agricultural and Biological Sciences",
                "Business, Management and Accounting",
                "Computer Science",
                "Decision Sciences",
                "Economics, Econometrics and Finance",
                "Energy",
                "Engineering",
                "Environmental Science",
                "Mathematics",
                "Multidisciplinary",
                "Psychology",
                "Social Sciences",
              ],
            },
            {
              label: "ABDC Rank",
              name: "abdc",
              options: ["A*", "A", "B", "C", "Not Applicable"],
            },
            {
              label: "ABS Rank",
              name: "abs",
              options: ["4*", "4", "3", "2", "1", "Not Applicable"],
            },
            {
              label: "Web of Science (JIF-Based Rank)",
              name: "wos_jif",
              options: [
                "Q1: Top 10% (90th percentile or above)",
                "Q1: 75th to 89th percentile",
                "Q2: 50th to 74th percentile",
                "Q3: 25th to 49th percentile",
                "Q4: 0 to 24th percentile",
                "Not Applicable",
              ],
            },
          ].map((d) => (
            <div key={d.name} style={{ marginTop: "10px" }}>
              <label style={{ fontWeight: "bold" }}>{d.label}</label>
              <select
                name={d.name}
                value={formData[d.name]}
                onChange={handleChange}
                style={inputStyle}
              >
                <option value="">Select</option>
                {d.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ))}

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Year",
                    "Title",
                    "Journal Name",
                    "DOI Link",
                    "Total Authors",
                    "Author Position",
                    "Collaborative?",
                    "Collaborative Author(s)",
                    "Scopus Rank (Quartile)",
                    "Scopus Category",
                    "ABDC Rank",
                    "ABS Rank",
                    "Web of Science (JIF-Based Rank)",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                  <th
                    style={{
                      border: "1px solid #ccc",
                      padding: "8px",
                      background: "#f1f5f9",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(rec.year)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.title}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.journal_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.doi_link}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.total_authors}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.author_position}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.is_collaborative}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.collaborative_authors}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.quartile}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.scopus_category}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.abdc}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.abs}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.wos_jif}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default JournalPublications;
